import { locationService } from './locationService';

interface PlanTrialRequest {
  planId: string;
  planName: string;
  planType?: string;
  userId?: string;
  country?: string;
  countryCode?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

interface PlanTrialResponse {
  success: boolean;
  message: string;
  requestId?: string;
}

class PlanTrialService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  async requestPlanTrial(request: PlanTrialRequest): Promise<PlanTrialResponse> {
    try {
      // Get user location for the request
      const location = await locationService.getLocationForTrialRequest();
      
      const requestData = {
        ...request,
        country: location.country,
        countryCode: location.countryCode,
        // Add UTM parameters from URL if available
        utm_source: request.utm_source || this.getUrlParameter('utm_source'),
        utm_medium: request.utm_medium || this.getUrlParameter('utm_medium'),
        utm_campaign: request.utm_campaign || this.getUrlParameter('utm_campaign'),
        utm_content: request.utm_content || this.getUrlParameter('utm_content'),
      };

      const response = await fetch(`${this.baseUrl}/plan-trial-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          message: 'Trial request submitted successfully! We will contact you soon.',
          requestId: result.id,
        };
      } else {
        const error = await response.json();
        return {
          success: false,
          message: error.message || 'Failed to submit trial request. Please try again.',
        };
      }
    } catch (error) {
      console.error('Error submitting plan trial request:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.',
      };
    }
  }

  // Helper method to get URL parameters
  private getUrlParameter(name: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  // Method to generate trial request for subscription plans
  async requestSubscriptionPlanTrial(planId: string, planName: string, planType: string): Promise<PlanTrialResponse> {
    return this.requestPlanTrial({
      planId,
      planName,
      planType,
      utm_source: 'mocktrail_app',
      utm_medium: 'subscription_page',
      utm_campaign: 'plan_trial',
      utm_content: planType,
    });
  }

  // Method to generate trial request for add-on plans
  async requestAddOnTrial(planId: string, planName: string): Promise<PlanTrialResponse> {
    return this.requestPlanTrial({
      planId,
      planName,
      planType: 'addon',
      utm_source: 'mocktrail_app',
      utm_medium: 'subscription_page',
      utm_campaign: 'addon_trial',
      utm_content: planName.toLowerCase(),
    });
  }

  // Method for landing page trial requests
  async requestLandingPageTrial(planId: string, planName: string, planType: string): Promise<PlanTrialResponse> {
    return this.requestPlanTrial({
      planId,
      planName,
      planType,
      utm_source: 'landing_page',
      utm_medium: 'pricing_card',
      utm_campaign: 'trial_request',
      utm_content: planType,
    });
  }
}

export const planTrialService = new PlanTrialService();
export type { PlanTrialRequest, PlanTrialResponse }; 